/// <amd-module name="Wit/BoardsHub/KanbanBoard/BoardKeyboardShortcuts" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { BoardAnnotationsIdentifiers } from "Wit/BoardsHub/Common/AnnotationUtils";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { IHotKeyEventType, IHotKeyEventService, IHotKeyEvent, IHotKeyEventData } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { isEmbedded, openWorkItemForm } from "./Components/Util";
import { IKanbanBoardService } from "./IKanbanBoardService";

export function registerKeyboardShortcuts(
    pageContext: IVssPageContext,
    onToggleFilterOn: () => void,
    toggleFullScreen: () => void) {
    const shortcutService = pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");

    if (isEmbedded()) {
        // theoretically we should restore the global shortcuts on leaving the page, but the point of removing the
        // global shortcuts is to keep the user from ever leaving the page in embedded mode
        shortcutService.removeShortcutGroup(Resources.KeyboardShortcutGroup_Global);
    }

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["n"],
        description: Resources.NewItem,
        action: () => {
            clickAddNewItemButton();
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["c"],
        description: Resources.NewChildItem,
        action: () => {
            const focusedWorkItem = getFocusedWorkItem(pageContext);
            if (!focusedWorkItem) {
                return;
            }

            const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            const enabledAnnotations = boardService.boardSettingsData.value?.annotationSettings.filter(setting => setting.isEnabled);
            if (!enabledAnnotations || enabledAnnotations.length === 0) {
                return;
            }

            let defaultAnnotation = enabledAnnotations.find(setting => setting.id === BoardAnnotationsIdentifiers.ChecklistAnnotation);
            if (!defaultAnnotation) {
                defaultAnnotation = enabledAnnotations.find(setting => setting.id.startsWith(BoardAnnotationsIdentifiers.CardAnnotationPrefix));
            }

            if (defaultAnnotation) {
                focusedWorkItem.setOpenAnnotation(defaultAnnotation.name, /** adding */ true);
            }
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["home"],
        description: Resources.FocusFirstItem,
        action: () => {
            const firstCard = document.querySelector(".wit-card") as HTMLElement | null;
            firstCard?.focus();
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["enter"],
        description: Resources.OpenItem,
        action: () => {
            const focusedItemId = getFocusedWorkItemId();

            if (focusedItemId) {
                openWorkItemForm(pageContext, focusedItemId);
            }
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+shift+f"],
        description: Resources.FilterResults,
        action: () => {
            onToggleFilterOn();

            triggerHotKeyEvent(pageContext, IHotKeyEventType.FilterToggled);
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+up"],
        description: Resources.MoveItemUp,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+down"],
        description: Resources.MoveItemDown,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+left"],
        description: Resources.MoveItemLeft,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+right"],
        description: Resources.MoveItemRight,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+home"],
        description: Resources.MoveItemToTopOfColumn,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+end"],
        description: Resources.MoveItemToBottomOfColumn,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+shift+up"],
        description: Resources.MoveItemToSwimlaneAbove,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["mod+shift+down"],
        description: Resources.MoveItemToSwimlaneBelow,
        action: () => {
            // this action is handled elsewhere, but we still want the shortcut to be documented
        },
        allowPropagation: true
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["f2"],
        description: Resources.RenameCard,
        action: () => {
            const focusedItem = getFocusedWorkItem(pageContext);
            if (focusedItem) {
                focusedItem.value.isTitleEditing.value = true;
            } else {
                tryToTriggerHotKeyEvent(pageContext, IHotKeyEventType.WorkItemChangeTitle);
            }
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["e"],
        description: Resources.ToggleEmptyFields,
        action: () => {
            tryToTriggerHotKeyEvent(pageContext, IHotKeyEventType.WorkItemToggleEmptyFields);
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["z"],
        description: Resources.ToggleFullScreenMode,
        action: () => {
            toggleFullScreen();
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["o"],
        description: Resources.ExpandAllSwimlanes,
        action: () => {
            const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            boardService.expandOrCollapseAllSwimlanes(true);
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["u"],
        description: Resources.CollapseAllSwimlanes,
        action: () => {
            const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            boardService.expandOrCollapseAllSwimlanes(false);
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["shift+pageup"],
        description: Resources.SelectNextSwimlaneAbove,
        action: () => {
            focusPreviousSwimlaneHeader();
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["shift+pagedown"],
        description: Resources.SelectNextSwimlaneBelow,
        action: () => {
            focusNextSwimlaneHeader();
        }
    });

    shortcutService.registerShortcut(Resources.KanbanBoardShortcutGroup, {
        combos: ["del"],
        description: Resources.Delete,
        action: () => {
            const focusedItem = getFocusedWorkItem(pageContext);
            if (focusedItem) {
                const projectName = focusedItem.value.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
                triggerHotKeyEvent(pageContext, IHotKeyEventType.WorkItemDelete, { workItemId: focusedItem.value.id, projectName });
            }
        }
    });
}

export function unregisterKeyboardShortcuts(pageContext: IVssPageContext) {
    const shortcutService = pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    shortcutService.removeShortcutGroup(Resources.KanbanBoardShortcutGroup);
}

function tryToTriggerHotKeyEvent(pageContext: IVssPageContext, eventType: IHotKeyEvent) {
    const focusedItemId = getFocusedWorkItemId();
    if (focusedItemId) {
        triggerHotKeyEvent(pageContext, eventType, { workItemId: focusedItemId });
    }
}

function triggerHotKeyEvent(pageContext: IVssPageContext, eventType: IHotKeyEvent, eventData?: IHotKeyEventData) {
    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
    hotKeyEventService.fireEvent(eventData ?? {}, eventType);
}

function getFocusedWorkItemId(): number | undefined {
    const focusedElement = document.activeElement as HTMLElement | undefined;
    if (!(focusedElement?.classList.contains("wit-card")
        || focusedElement?.classList.contains("annotation-row"))) {
        return;
    }

    const itemId = Number(focusedElement.dataset.itemid);
    if (!itemId || itemId <= 0) {
        return;
    }

    return itemId;
}

function getFocusedWorkItem(pageContext: IVssPageContext): IObservableBoardsWorkItem | undefined {
    const itemId = getFocusedWorkItemId();
    if (!itemId) {
        return;
    }

    const workItemService = pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
    return workItemService.get(itemId);
}

/**
 * Focus the first swimlane header or the previous swimlane header if a swimlane header is currently focused
 */
function focusPreviousSwimlaneHeader(): void {
    const focusedSwimlane = getFocusedSwimlaneHeader();
    const swimlaneHeaders = Array.from(document.querySelectorAll<HTMLElement>(".kanban-board-row-header"));
    if (swimlaneHeaders.length === 0) {
        return;
    }

    if (focusedSwimlane) {
        // focus the previous swimlane
        const currentIndex = swimlaneHeaders.findIndex(header => header === focusedSwimlane);
        if (currentIndex > 0) {
            const nextHeader = swimlaneHeaders[currentIndex - 1];
            nextHeader.focus();
        }
    } else {
        // focus the first swimlane
        swimlaneHeaders[0].focus();
    }
}

/**
 * Focus the first swimlane header or the previous swimlane header if a swimlane header is currently focused
 */
function focusNextSwimlaneHeader(): void {
    const focusedSwimlane = getFocusedSwimlaneHeader();
    const swimlaneHeaders = Array.from(document.querySelectorAll<HTMLElement>(".kanban-board-row-header"));
    if (swimlaneHeaders.length === 0) {
        return;
    }

    if (focusedSwimlane) {
        // focus the next swimlane
        const currentIndex = swimlaneHeaders.findIndex(header => header === focusedSwimlane);
        if (currentIndex < swimlaneHeaders.length - 1) {
            const nextHeader = swimlaneHeaders[currentIndex + 1];
            nextHeader.focus();
        }
    } else {
        // focus the last swimlane
        swimlaneHeaders[swimlaneHeaders.length - 1].focus();
    }
}

function getFocusedSwimlaneHeader(): HTMLElement | null {
    let currentElement = document.activeElement as HTMLElement | null;
    while (currentElement) {
        if (currentElement.classList.contains("kanban-board-row-header")) {
            break;
        } else {
            currentElement = currentElement.parentElement;
        }
    }

    return currentElement;
}

/**
 * fire a click event to the add new item button
 */
function clickAddNewItemButton(): void {
    const addNewItemButton = document.querySelector<HTMLElement>(".add-new-item-button");
    addNewItemButton?.click();
}